import React, { useEffect, useMemo, useState } from "react";
import { Thermometer, Drop, Sun, Clock } from "@phosphor-icons/react";
import { getHourly } from "@/lib/weather";

const WIDTH = 300;
const HEIGHT = 44;
const PAD_X = 2;
const PAD_Y = 6;

function Sparkline({ values, nowIndex, color, fillColor, hoverIndex, onHover, fmt, unit, hours }) {
  const valid = values.map((v) => (v == null || Number.isNaN(v) ? null : v));
  const nums = valid.filter((v) => v != null);
  if (nums.length < 2) return null;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const n = valid.length;
  const stepX = (WIDTH - PAD_X * 2) / (n - 1);

  const yFor = (v) => {
    if (v == null) return null;
    const t = (v - min) / span;
    return HEIGHT - PAD_Y - t * (HEIGHT - PAD_Y * 2);
  };

  const pts = valid.map((v, i) => {
    const y = yFor(v);
    if (y == null) return null;
    return [PAD_X + i * stepX, y];
  });

  // Build line path, skipping gaps
  let linePath = "";
  pts.forEach((p, i) => {
    if (!p) return;
    linePath += (linePath && pts[i - 1] ? "L" : "M") + p[0].toFixed(1) + "," + p[1].toFixed(1) + " ";
  });

  // Area path (closes to bottom)
  const firstIdx = pts.findIndex(Boolean);
  const lastIdx = pts.map((p, i) => (p ? i : -1)).reduce((a, b) => (b > a ? b : a), -1);
  let areaPath = "";
  if (firstIdx >= 0 && lastIdx >= 0) {
    areaPath =
      `M${pts[firstIdx][0].toFixed(1)},${HEIGHT - PAD_Y + 0.5} ` +
      pts
        .filter(Boolean)
        .map((p) => `L${p[0].toFixed(1)},${p[1].toFixed(1)}`)
        .join(" ") +
      ` L${pts[lastIdx][0].toFixed(1)},${HEIGHT - PAD_Y + 0.5} Z`;
  }

  const nowX = nowIndex != null && nowIndex >= 0 && nowIndex < n ? PAD_X + nowIndex * stepX : null;
  const nowY = nowX != null ? yFor(valid[nowIndex]) : null;

  const hoverPt = hoverIndex != null && hoverIndex >= 0 && hoverIndex < n ? pts[hoverIndex] : null;
  const hoverVal = hoverPt ? valid[hoverIndex] : null;
  const hoverHour = hoverPt && hours?.[hoverIndex] ? hourLabel(hours[hoverIndex].time) : null;

  // Pointer handler: convert pointer X -> hour index
  const handleMove = (e) => {
    if (!onHover) return;
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    if (clientX == null) return;
    const rel = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, rel / rect.width));
    const idx = Math.round(ratio * (n - 1));
    onHover(idx);
  };
  const handleLeave = () => onHover?.(null);

  // Tooltip label placement — flip above or below depending on y position
  const tooltipText = hoverPt && hoverVal != null ? `${hoverHour || ""} · ${fmt(hoverVal)}${unit || ""}` : null;
  const tooltipAbove = hoverPt ? hoverPt[1] > HEIGHT / 2 : true;
  const tooltipY = hoverPt ? (tooltipAbove ? hoverPt[1] - 7 : hoverPt[1] + 11) : 0;
  // Estimate text width via char count × avg px — SVG renders text natively so we can let it flow but want a nice background pill
  const estW = tooltipText ? Math.max(42, tooltipText.length * 5.2 + 10) : 0;
  let tooltipX = hoverPt ? hoverPt[0] : 0;
  tooltipX = Math.max(estW / 2 + 2, Math.min(WIDTH - estW / 2 - 2, tooltipX));

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full h-11 cursor-crosshair touch-none"
      preserveAspectRatio="none"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onTouchStart={handleMove}
      onTouchMove={handleMove}
      onTouchEnd={handleLeave}
      role="img"
      aria-label="hourly trend"
    >
      <path d={areaPath} fill={fillColor} opacity="0.35" />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      {nowX != null && hoverIndex == null && (
        <>
          <line x1={nowX} y1={PAD_Y - 2} x2={nowX} y2={HEIGHT - PAD_Y + 2} stroke={color} strokeWidth="1" strokeDasharray="2,2" opacity="0.5" />
          {nowY != null && <circle cx={nowX} cy={nowY} r="2.6" fill={color} />}
        </>
      )}
      {hoverPt && (
        <>
          <line x1={hoverPt[0]} y1={PAD_Y - 2} x2={hoverPt[0]} y2={HEIGHT - PAD_Y + 2} stroke={color} strokeWidth="1" opacity="0.75" />
          <circle cx={hoverPt[0]} cy={hoverPt[1]} r="3.1" fill="#fff" stroke={color} strokeWidth="1.6" />
          {tooltipText && (
            <g pointerEvents="none" data-testid="hourly-tooltip">
              <rect
                x={tooltipX - estW / 2}
                y={tooltipY - 8}
                width={estW}
                height={12}
                rx={3}
                ry={3}
                fill="#1F2721"
                opacity="0.92"
              />
              <text
                x={tooltipX}
                y={tooltipY + 1}
                textAnchor="middle"
                fontSize="8.5"
                fontFamily="ui-monospace, Menlo, monospace"
                fill="#fff"
              >
                {tooltipText}
              </text>
            </g>
          )}
        </>
      )}
    </svg>
  );
}

function hourLabel(iso) {
  // "2026-10-05T14:00" -> "2 PM"
  try {
    const parts = iso.split("T")[1].split(":");
    const h = parseInt(parts[0], 10);
    if (Number.isNaN(h)) return iso;
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12} ${suffix}`;
  } catch {
    return iso;
  }
}

export default function HourlyTrend({ lat, lon }) {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState("");

  useEffect(() => {
    if (lat == null || lon == null) {
      setHours([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setWarning("");
      try {
        const data = await getHourly(lat, lon);
        if (cancelled) return;
        setHours(data.hours || []);
        if (data.warning) setWarning(data.warning);
      } catch {
        if (!cancelled) setWarning("Hourly data temporarily unavailable.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, lon]);

  const nowIndex = useMemo(() => {
    if (hours.length === 0) return -1;
    const nowH = new Date().getHours();
    // Open-Meteo returns 24 entries for today (local tz). Match by hour-of-day.
    let bestIdx = 0;
    let bestDiff = Infinity;
    hours.forEach((h, i) => {
      try {
        const hh = parseInt(h.time.split("T")[1].split(":")[0], 10);
        const diff = Math.abs(hh - nowH);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
        }
      } catch {
        // ignore
      }
    });
    return bestIdx;
  }, [hours]);

  const nowRow = hours[nowIndex];
  const temps = hours.map((h) => h.temp_c);
  const hums = hours.map((h) => h.humidity_pct);
  const uvs = hours.map((h) => h.uv_index);

  // Shared hover index across all three sparklines
  const [hoverIndex, setHoverIndex] = useState(null);
  const activeIndex = hoverIndex != null ? hoverIndex : nowIndex;
  const activeRow = hours[activeIndex];
  const headerLabel = hoverIndex != null ? hourLabel(hours[hoverIndex]?.time || "") : nowRow ? `now ${hourLabel(nowRow.time)}` : "";

  return (
    <div className="glass rounded-2xl p-4" data-testid="hourly-trend">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-data text-mute flex items-center gap-1">
          <Clock size={12} weight="duotone" /> today · hourly
        </div>
        {headerLabel && (
          <div className="text-xs text-mute" data-testid="hourly-now-label">
            {headerLabel}
          </div>
        )}
      </div>

      {loading && hours.length === 0 && (
        <div className="text-sm text-mute animate-pulse-soft">loading hourly…</div>
      )}
      {!loading && warning && hours.length === 0 && (
        <div className="text-sm text-terra">{warning}</div>
      )}

      {hours.length > 0 && (
        <div className="space-y-2.5" data-testid="hourly-grid">
          <TrendRow
            Icon={Thermometer}
            label="Temp"
            unit="°"
            currentValue={activeRow?.temp_c}
            values={temps}
            nowIndex={nowIndex}
            hoverIndex={hoverIndex}
            onHover={setHoverIndex}
            hours={hours}
            color="#C76A3E"
            fillColor="#D9A05B"
            fmt={(v) => Math.round(v)}
          />
          <TrendRow
            Icon={Drop}
            label="Humidity"
            unit="%"
            currentValue={activeRow?.humidity_pct}
            values={hums}
            nowIndex={nowIndex}
            hoverIndex={hoverIndex}
            onHover={setHoverIndex}
            hours={hours}
            color="#2F6D5C"
            fillColor="#4FA591"
            fmt={(v) => Math.round(v)}
          />
          <TrendRow
            Icon={Sun}
            label="UV"
            unit=""
            currentValue={activeRow?.uv_index}
            values={uvs}
            nowIndex={nowIndex}
            hoverIndex={hoverIndex}
            onHover={setHoverIndex}
            hours={hours}
            color="#B98B3A"
            fillColor="#E6C27A"
            fmt={(v) => (v >= 10 ? Math.round(v) : v.toFixed(1))}
          />
        </div>
      )}
    </div>
  );
}


function TrendRow({ Icon, label, unit, currentValue, values, nowIndex, hoverIndex, onHover, hours, color, fillColor, fmt }) {
  const valid = values.filter((v) => v != null && !Number.isNaN(v));
  const min = valid.length ? Math.min(...valid) : null;
  const max = valid.length ? Math.max(...valid) : null;
  return (
    <div className="grid grid-cols-[72px_1fr_72px] items-center gap-2">
      <div className="flex items-center gap-1 text-mute">
        <Icon size={12} weight="duotone" />
        <span className="font-mono-data text-[11px] uppercase">{label}</span>
      </div>
      <div className="relative">
        <Sparkline
          values={values}
          nowIndex={nowIndex}
          hoverIndex={hoverIndex}
          onHover={onHover}
          hours={hours}
          fmt={fmt}
          unit={unit}
          color={color}
          fillColor={fillColor}
        />
      </div>
      <div className="text-right leading-tight">
        <div className="font-display text-sm text-ink" data-testid={`hourly-current-${label.toLowerCase()}`}>
          {currentValue != null ? fmt(currentValue) : "—"}
          <span className="text-mute text-xs ml-0.5">{unit}</span>
        </div>
        {min != null && max != null && (
          <div className="font-mono-data text-[10px] text-mute">
            {fmt(min)}–{fmt(max)}{unit}
          </div>
        )}
      </div>
    </div>
  );
}
